import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Layers, Plus, Trash2, RotateCcw, Save, Copy, Info, Rocket } from "lucide-react";

const TEMPLATE_KEYS = ["U6", "U8", "U10", "U12", "U14", "U16", "JUVENIL", "SENIOR", "PORTERO"];
const RATING = ["Nunca", "A veces", "A menudo", "Casi siempre", "Siempre"];

const slug = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
const newIndicatorId = (blockId) => `${blockId || "ind"}_${Math.random().toString(36).slice(2, 7)}`;
const newBlockId = () => `block_${Math.random().toString(36).slice(2, 7)}`;

export default function ClubTemplates() {
  const [current, setCurrent] = useState("U12");
  const [teams, setTeams] = useState([]);
  const [scopeTeamId, setScopeTeamId] = useState(""); // empty = club-wide
  const [template, setTemplate] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Plan gating: fetch our club's plan + the per-plan template edit gates
  const [planGate, setPlanGate] = useState({ plan: "presencia", club_level: false, team_level: false });

  const loadTemplate = async (key, teamId) => {
    setLoading(true);
    try {
      const params = teamId ? `?team_id=${teamId}` : "";
      const { data } = await api.get(`/templates/${key}${params}`);
      setTemplate(data);
      const exp = {};
      (data.blocks || []).forEach((b, i) => { exp[b.id] = i === 0; });
      setExpanded(exp);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadOverrides = async () => {
    try {
      const { data } = await api.get("/templates-overrides");
      setOverrides(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [t] = await Promise.all([api.get("/teams"), loadOverrides()]);
        setTeams(t.data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  // Fetch the club's plan gate for template editing (Presencia/Club/Total)
  useEffect(() => {
    (async () => {
      try {
        const [planRes, plansRes] = await Promise.all([
          api.get("/clubs/me/plan"),
          api.get("/plans"),
        ]);
        const plan = planRes.data?.plan || "presencia";
        const found = (plansRes.data || []).find(p => p.id === plan);
        const gates = found?.template_gates || { club_level: false, team_level: false };
        setPlanGate({ plan, ...gates });
      } catch { /* fall back to defaults (locked) */ }
    })();
  }, []);

  useEffect(() => { loadTemplate(current, scopeTeamId); }, [current, scopeTeamId]);

  const hasOverride = useMemo(() => {
    const key = current.toUpperCase();
    return overrides.some(o =>
      o.template_key === key &&
      ((scopeTeamId && o.team_id === scopeTeamId) || (!scopeTeamId && !o.team_id))
    );
  }, [overrides, current, scopeTeamId]);

  const save = async () => {
    setSaving(true);
    try {
      const params = scopeTeamId ? `?team_id=${scopeTeamId}` : "";
      await api.put(`/templates/${current}${params}`, template);
      toast.success(scopeTeamId ? "Plantilla del equipo guardada" : "Plantilla del club guardada");
      loadOverrides();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!confirm(scopeTeamId
      ? "¿Eliminar personalización de este equipo y volver a la plantilla del club?"
      : "¿Restaurar la plantilla por defecto del sistema para todo el club? Se perderán los cambios personalizados."
    )) return;
    try {
      const params = scopeTeamId ? `?team_id=${scopeTeamId}` : "";
      await api.delete(`/templates/${current}${params}`);
      toast.success("Restaurada");
      loadOverrides();
      loadTemplate(current, scopeTeamId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ------- Block operations -------
  const addBlock = () => {
    const id = newBlockId();
    setTemplate((t) => ({ ...t, blocks: [...(t.blocks || []), { id, name: "Nuevo bloque", indicators: [] }] }));
    setExpanded((e) => ({ ...e, [id]: true }));
  };
  const removeBlock = (bi) => {
    if (!confirm("¿Eliminar este bloque y todos sus indicadores?")) return;
    setTemplate((t) => ({ ...t, blocks: t.blocks.filter((_, i) => i !== bi) }));
  };
  const updateBlockName = (bi, name) => {
    setTemplate((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) => (i === bi ? { ...b, name } : b)),
    }));
  };

  // ------- Indicator operations -------
  const addIndicator = (bi) => {
    setTemplate((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) => i === bi ? {
        ...b,
        indicators: [...b.indicators, { id: newIndicatorId(b.id), name: "Nuevo indicador", has_intention: false, has_success: false }],
      } : b),
    }));
  };
  const removeIndicator = (bi, ii) => {
    setTemplate((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) => i === bi ? {
        ...b, indicators: b.indicators.filter((_, k) => k !== ii),
      } : b),
    }));
  };
  const updateIndicator = (bi, ii, patch) => {
    setTemplate((t) => ({
      ...t,
      blocks: t.blocks.map((b, i) => i === bi ? {
        ...b, indicators: b.indicators.map((ind, k) => k === ii ? { ...ind, ...patch } : ind),
      } : b),
    }));
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline">Configuración avanzada</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Plantillas de Análisis</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Añade, edita o elimina bloques e indicadores para cada categoría. Puedes definir una plantilla común para el club o una específica por equipo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(() => {
            const canEdit = scopeTeamId ? planGate.team_level : planGate.club_level;
            const lockTitle = canEdit ? undefined : `Requiere ${scopeTeamId ? "Plan Total" : "Plan Club o superior"}`;
            return (
              <>
                <button data-testid="tpl-reset-btn" onClick={resetToDefault} disabled={!canEdit} title={lockTitle}
                        className="inline-flex items-center gap-2 h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:text-muted-foreground">
                  <RotateCcw size={13} /> Restaurar por defecto
                </button>
                <button data-testid="save-template-btn" disabled={saving || !canEdit} onClick={save} title={lockTitle} className="btn-volt disabled:opacity-40 disabled:cursor-not-allowed">
                  <Save size={14} /> {saving ? "Guardando…" : "Guardar cambios"}
                </button>
              </>
            );
          })()}
        </div>
      </div>

      {/* Scope selector */}
      <div className="border border-border p-4 mb-4 bg-white/[0.02]">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Categoría / Plantilla</label>
            <div className="flex gap-2 flex-wrap">
              {TEMPLATE_KEYS.map((k) => (
                <button
                  key={k}
                  data-testid={`tpl-tab-${k}`}
                  onClick={() => setCurrent(k)}
                  className={`px-4 h-10 border font-heading text-sm uppercase tracking-wider transition-colors duration-200 ${
                    current === k ? "bg-primary text-white border-primary" : "border-border hover:border-primary"
                  }`}
                >{k}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Aplicar a</label>
            <select
              data-testid="tpl-scope-select"
              value={scopeTeamId}
              onChange={(e) => setScopeTeamId(e.target.value)}
              className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none"
            >
              <option value="">📋 Todo el club (plantilla general)</option>
              {planGate.team_level && teams.map((t) => <option key={t.id} value={t.id}>🎯 {t.name} (equipo específico)</option>)}
            </select>
            <div className={`mt-2 text-[11px] flex items-center gap-1.5 ${hasOverride ? "text-amber-400" : "text-muted-foreground"}`}>
              <Info size={11} />
              {hasOverride
                ? (scopeTeamId ? "Este equipo tiene una plantilla personalizada" : "El club tiene una plantilla personalizada")
                : "Usando la plantilla por defecto del sistema"}
            </div>
            {/* Plan gating notice — friendly upsell CTA that turns the silent lock into a sales moment */}
            {!planGate.club_level && (
              <div className="mt-3 border border-primary/40 bg-gradient-to-r from-primary/10 to-primary/5 p-4 flex items-start gap-3" data-testid="tpl-plan-lock-club">
                <Rocket size={18} className="text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-heading font-black uppercase text-sm text-primary">Actualiza a Plan Club</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    La edición de plantillas de análisis está disponible desde el <b>Plan Club</b>. En Presencia se usa la plantilla del sistema por defecto.
                  </p>
                </div>
                <Link to="/billing" data-testid="tpl-lock-upgrade-club-btn"
                  className="shrink-0 h-9 px-4 bg-primary text-black hover:bg-primary/90 text-[11px] uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors">
                  Ver planes
                </Link>
              </div>
            )}
            {planGate.club_level && !planGate.team_level && scopeTeamId && (
              <div className="mt-3 border border-primary/40 bg-gradient-to-r from-primary/10 to-primary/5 p-4 flex items-start gap-3" data-testid="tpl-plan-lock-team">
                <Rocket size={18} className="text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-heading font-black uppercase text-sm text-primary">Actualiza a Plan Total</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Las <b>plantillas personalizadas por equipo</b> están disponibles solo en el <b>Plan Total</b>. Con tu plan actual puedes editar la plantilla común del club para toda la categoría.
                  </p>
                </div>
                <Link to="/billing" data-testid="tpl-lock-upgrade-team-btn"
                  className="shrink-0 h-9 px-4 bg-primary text-black hover:bg-primary/90 text-[11px] uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors">
                  Actualizar plan
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>}

      {!loading && template && (
        <div>
          <div className="mb-4 p-4 border border-border">
            <div className="overline">Plantilla</div>
            <input
              data-testid="tpl-name-input"
              value={template.name || ""}
              onChange={(e) => setTemplate((t) => ({ ...t, name: e.target.value }))}
              className="w-full font-heading font-black text-2xl bg-transparent border-0 border-b border-border/60 focus:border-primary focus:outline-none py-1 mt-1"
            />
            <textarea
              data-testid="tpl-desc-input"
              value={template.description || ""}
              onChange={(e) => setTemplate((t) => ({ ...t, description: e.target.value }))}
              rows={2}
              className="w-full mt-2 bg-transparent border border-border/60 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="Descripción / foco de la categoría"
            />
            <div className="overline mt-3">Escala 0-4: {RATING.join(" · ")}</div>
          </div>

          <div className="space-y-2">
            {(template.blocks || []).map((b, bi) => (
              <div key={b.id} className="border border-border" data-testid={`tpl-block-${b.id}`}>
                <div className="flex items-center gap-2 p-3 border-b border-border">
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [b.id]: !e[b.id] }))}
                    data-testid={`block-toggle-${b.id}`}
                    className="hover:text-primary transition-colors"
                  >
                    {expanded[b.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <Layers size={14} className="text-primary" />
                  <input
                    data-testid={`block-name-${b.id}`}
                    value={b.name}
                    onChange={(e) => updateBlockName(bi, e.target.value)}
                    className="flex-1 h-8 bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary font-heading font-bold uppercase tracking-tight text-sm focus:outline-none px-2"
                  />
                  <span className="overline text-[10px] text-muted-foreground">{b.indicators.length} ind.</span>
                  <button
                    data-testid={`block-remove-${b.id}`}
                    onClick={() => removeBlock(bi)}
                    className="h-7 w-7 border border-border hover:border-red-400 hover:text-red-400 flex items-center justify-center transition-colors"
                    title="Eliminar bloque"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                {expanded[b.id] && (
                  <div className="p-3 space-y-2">
                    {b.indicators.map((ind, ii) => (
                      <div key={ind.id} className="flex items-center gap-2 flex-wrap" data-testid={`ind-row-${ind.id}`}>
                        <span className="font-mono text-[10px] text-muted-foreground w-20 truncate" title={ind.id}>{ind.id}</span>
                        <input
                          data-testid={`ind-name-${ind.id}`}
                          value={ind.name}
                          onChange={(e) => updateIndicator(bi, ii, { name: e.target.value })}
                          className="flex-1 min-w-[200px] h-9 bg-transparent border border-border px-3 text-sm focus:border-primary focus:outline-none"
                        />
                        <label className="flex items-center gap-1.5 text-[10px] overline cursor-pointer whitespace-nowrap" title="INT · Intentos. Marca esta casilla si quieres contar cuántas veces el jugador INTENTÓ esta acción en el partido (ej: cuántos regates intentó). Déjalo sin marcar para actitud, hábitos u observaciones que no se pueden contar.">
                          <input
                            type="checkbox"
                            data-testid={`ind-int-${ind.id}`}
                            checked={!!ind.has_intention}
                            onChange={(e) => updateIndicator(bi, ii, { has_intention: e.target.checked })}
                            className="accent-primary"
                          />
                          INT · Intentos
                        </label>
                        <label className="flex items-center gap-1.5 text-[10px] overline cursor-pointer whitespace-nowrap" title="S/N · Éxitos (Sí/No). Marca esta casilla junto con INT si además puedes contar cuántas veces la acción salió BIEN. El sistema calculará automáticamente el % de acierto (ej: 5 regates exitosos de 8 intentos = 62,5%).">
                          <input
                            type="checkbox"
                            data-testid={`ind-succ-${ind.id}`}
                            checked={!!ind.has_success}
                            onChange={(e) => updateIndicator(bi, ii, { has_success: e.target.checked })}
                            className="accent-primary"
                          />
                          S/N · Éxitos
                        </label>
                        <button
                          data-testid={`ind-remove-${ind.id}`}
                          onClick={() => removeIndicator(bi, ii)}
                          className="h-8 w-8 border border-border hover:border-red-400 hover:text-red-400 flex items-center justify-center transition-colors"
                          title="Eliminar indicador"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    <button
                      data-testid={`block-add-ind-${b.id}`}
                      onClick={() => addIndicator(bi)}
                      className="mt-2 inline-flex items-center gap-2 h-9 px-3 border border-dashed border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider transition-colors"
                    >
                      <Plus size={12} /> Añadir indicador
                    </button>
                    <div className="mt-3 text-[11px] text-muted-foreground border-l-2 border-primary/40 pl-3 max-w-2xl">
                      <div className="font-heading uppercase tracking-wider text-primary text-[10px] mb-1">Cómo usar INT / S/N</div>
                      <div>Cada indicador se puntúa siempre de <b>0 a 4</b>. Las casillas son OPCIONALES:</div>
                      <ul className="mt-1 space-y-0.5 list-disc list-inside">
                        <li><b>INT · Intentos</b> — marca solo si puedes contar cuántas veces el jugador intentó la acción (ej: regates, pases filtrados, golpeos).</li>
                        <li><b>S/N · Éxitos</b> — marca junto con INT si además puedes contar cuántas salieron bien. El sistema calcula el % de acierto automáticamente.</li>
                        <li><b>Actitud, hábitos, esfuerzo</b> → NO marques ninguna. Solo con la nota 0-4 es suficiente.</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button
              data-testid="tpl-add-block-btn"
              onClick={addBlock}
              disabled={scopeTeamId ? !planGate.team_level : !planGate.club_level}
              title={(scopeTeamId ? !planGate.team_level : !planGate.club_level) ? `Requiere ${scopeTeamId ? "Plan Total" : "Plan Club o superior"}` : undefined}
              className="w-full inline-flex items-center justify-center gap-2 h-11 border border-dashed border-primary/60 hover:border-primary hover:bg-primary/5 text-primary text-xs uppercase font-heading tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <Plus size={13} /> Añadir bloque
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
